import { animated, useSpring } from "@react-spring/web"
import { useCallback, useEffect, useRef } from "react"

import { ArrowLeftIcon } from "@/components/shared"
import { Button } from "@/components/ui"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { sendHaptic } from "@/modules/helpers/utils"

type GoBackProps = {
  className?: string
  onClick?: () => void
  title?: string
  hidden?: boolean
  enabled?: boolean
}

const GoBack: React.FC<GoBackProps> = ({
  className,
  onClick,
  title = "Quay lại",
  hidden = false,
  enabled = true
}) => {
  // Spring animation for arrow indicator
  const [{ opacity, scale, x }, api] = useSpring(() => ({
    opacity: 0,
    scale: 0.5,
    x: -20,
    config: { tension: 300, friction: 30 }
  }))

  const handleBack = useCallback(() => {
    if (!enabled) return
    sendHaptic()
    if (onClick) onClick?.()
    else window.history.back()
  }, [enabled, onClick])

  // Helper function to check if element or its parents are scrollable horizontally
  const isScrollableElement = (element: HTMLElement | null): boolean => {
    if (!element) return false

    let current: HTMLElement | null = element
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current)
      const overflowX = style.overflowX
      const overflow = style.overflow

      // Check if element can scroll horizontally
      if (
        (overflowX === "scroll" ||
          overflowX === "auto" ||
          overflow === "scroll" ||
          overflow === "auto") &&
        current.scrollWidth > current.clientWidth
      ) {
        return true
      }

      // Check for common scrollable classes/attributes
      if (
        current.classList.contains("overflow-x-auto") ||
        current.classList.contains("overflow-x-scroll") ||
        current.classList.contains("scrollbar-hide") ||
        current.hasAttribute("data-scrollable") ||
        current.tagName === "SLIDER" ||
        current.getAttribute("role") === "slider" ||
        current.getAttribute("role") === "region" ||
        // Check for carousel/slider libraries
        current.classList.contains("embla") ||
        current.classList.contains("swiper") ||
        current.classList.contains("carousel") ||
        current.classList.contains("slider") ||
        current.querySelector(".embla") ||
        current.querySelector(".swiper") ||
        current.querySelector("[data-carousel]") ||
        current.querySelector("[data-slider]")
      ) {
        return true
      }

      current = current.parentElement
    }
    return false
  }

  // Native touch event handlers
  const touchStartRef = useRef<{ x: number; y: number; target: HTMLElement | null } | null>(null)
  const isActiveRef = useRef(false)

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // Skip if not enabled
      if (!enabled) return

      const touch = e.touches[0]
      if (!touch) return

      const startX = touch.clientX
      const startY = touch.clientY
      const target = e.target as HTMLElement

      // Allow gesture from anywhere on screen
      // Check if target is scrollable horizontally - if so, don't capture
      if (isScrollableElement(target)) return

      touchStartRef.current = { x: startX, y: startY, target }
      isActiveRef.current = false
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartRef.current) return

      const touch = e.touches[0]
      if (!touch) return

      const currentX = touch.clientX
      const currentY = touch.clientY
      const deltaX = currentX - touchStartRef.current.x
      const deltaY = currentY - touchStartRef.current.y
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)

      // Only allow rightward movement
      if (deltaX < 0) {
        touchStartRef.current = null
        api.start({ opacity: 0, scale: 0.5, x: -20, immediate: true })
        return
      }

      // Cancel if this looks like a vertical scroll (more strict to allow horizontal swipe from anywhere)
      if (absDeltaY > absDeltaX * 0.7 && absDeltaY > 15) {
        touchStartRef.current = null
        api.start({ opacity: 0, scale: 0.5, x: -20, immediate: true })
        return
      }

      // Only process if horizontal movement is significant
      if (absDeltaX < 10) {
        return
      }

      // Check if current touch target or original target is scrollable
      const currentTarget = document.elementFromPoint(currentX, currentY) as HTMLElement
      if (
        (touchStartRef.current.target && isScrollableElement(touchStartRef.current.target)) ||
        (currentTarget && isScrollableElement(currentTarget))
      ) {
        touchStartRef.current = null
        api.start({ opacity: 0, scale: 0.5, x: -20, immediate: true })
        return
      }

      // Show arrow indicator
      isActiveRef.current = true
      const clampedMx = Math.max(0, deltaX)
      const maxDistance = 150
      const progress = Math.min(clampedMx / maxDistance, 1)

      // Show arrow even with small movement
      if (clampedMx > 5) {
        api.start({
          opacity: progress,
          scale: 0.5 + progress * 0.5,
          x: -20 + clampedMx * 0.3,
          immediate: true
        })
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) {
        return
      }

      const touch = e.changedTouches[0]
      if (!touch) {
        touchStartRef.current = null
        isActiveRef.current = false
        api.start({ opacity: 0, scale: 0.5, x: -20 })
        return
      }

      const currentX = touch.clientX
      const currentY = touch.clientY
      const deltaX = currentX - touchStartRef.current.x
      const deltaY = currentY - touchStartRef.current.y
      const absDeltaX = Math.abs(deltaX)
      const absDeltaY = Math.abs(deltaY)
      const clampedMx = Math.max(0, deltaX)
      const threshold = 100 // Reduced threshold - easier to trigger

      // Calculate velocity (simple approximation)
      const timestamp = (touchStartRef.current as any).timestamp || Date.now()
      const time = Date.now() - timestamp
      const velocity = time > 0 ? absDeltaX / time : 0

      // Final check: ensure we're not on a scrollable element
      const endTarget = document.elementFromPoint(currentX, currentY) as HTMLElement
      if (endTarget && isScrollableElement(endTarget)) {
        touchStartRef.current = null
        isActiveRef.current = false
        api.start({ opacity: 0, scale: 0.5, x: -20 })
        return
      }

      // Only trigger back if:
      // 1. Swiped more than threshold (required)
      // 2. AND horizontal movement is significantly more than vertical (at least 1.5x)
      // 3. OR very high velocity swipe (more than 1px/ms) with at least 50px movement
      const isHorizontalSwipe = absDeltaX > absDeltaY * 1.5

      if (
        (clampedMx > threshold && isHorizontalSwipe) ||
        (velocity > 1.0 && clampedMx > 50 && isHorizontalSwipe)
      ) {
        handleBack()
      }

      // Reset
      touchStartRef.current = null
      isActiveRef.current = false
      api.start({ opacity: 0, scale: 0.5, x: -20 })
    }

    // Enhanced touchStart with timestamp
    const enhancedHandleTouchStart = (e: TouchEvent) => {
      handleTouchStart(e)
      if (touchStartRef.current) {
        ;(touchStartRef.current as any).timestamp = Date.now()
      }
    }

    document.addEventListener("touchstart", enhancedHandleTouchStart, { passive: true })
    document.addEventListener("touchmove", handleTouchMove, { passive: true })
    document.addEventListener("touchend", handleTouchEnd, { passive: true })
    document.addEventListener("touchcancel", handleTouchEnd, { passive: true })

    return () => {
      document.removeEventListener("touchstart", enhancedHandleTouchStart)
      document.removeEventListener("touchmove", handleTouchMove)
      document.removeEventListener("touchend", handleTouchEnd)
      document.removeEventListener("touchcancel", handleTouchEnd)
    }
  }, [api, handleBack, enabled])

  return (
    <>
      {/* Swipe indicator arrow */}
      <animated.div
        className="fixed left-4 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
        style={{
          opacity,
          transform: x.to((x) => `translateX(${x}px) translateY(-50%)`)
        }}
      >
        <animated.div
          style={{
            transform: scale.to((s) => `scale(${s})`)
          }}
          className="bg-white rounded-full p-2.5 backdrop-blur-sm shadow-lg flex items-center justify-center"
        >
          <ArrowLeftIcon size={30} />
        </animated.div>
      </animated.div>
      {!hidden && (
        <div className={cn("sticky top-0 z-[60] w-full pointer-events-auto", className)}>
          <div
            className="flex items-center gap-1 px-4 py-3 pointer-events-auto w-fit"
            onClick={handleBack}
          >
            <Button variant="ghost" size="sm" className="rounded-full w-8 h-8 pointer-events-auto">
              <ArrowLeftIcon size={30} />
            </Button>
            <p className="text-sm text-black">{title}</p>
          </div>
        </div>
      )}
    </>
  )
}

export { GoBack }
